import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from { opacity: 0 }
  to { opacity: 1 }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(8px) scale(0.98) }
  to { opacity: 1; transform: translateY(0) scale(1) }
`;

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(20, 28, 42, 0.55);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease;
`;

export const ModalContainer = styled.div`
  background: #fff;
  border-radius: 8px;
  width: 620px;
  max-width: calc(100vw - 40px);
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  animation: ${popIn} 0.22s ease;
  position: relative;
  font-family: ${({ theme }) => theme.fonts.sfcompact_text}, system-ui,
    sans-serif;

  @media (max-width: 640px) {
    width: 100%;
    max-width: calc(100vw - 24px);
  }
`;

export const Stripe = styled.div`
  height: 4px;
  background: ${({ theme }) => theme.colors['terra-cotta']};
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 5;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border-radius: 50%;
  border: 0;
  color: ${({ theme }) => theme.colors.steel};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors['white-two']};
    color: ${({ theme }) => theme.colors.dark};
  }
`;

export const Body = styled.div`
  padding: 36px 44px 30px;

  @media (max-width: 640px) {
    padding: 30px 24px 24px;
  }
`;

export const Eyebrow = styled.div`
  font-family: ${({ theme }) => theme.fonts.sfcompact_semibold}, system-ui,
    sans-serif;
  font-size: 11px;
  letter-spacing: 0.14em;
  font-weight: 700;
  color: ${({ theme }) => theme.colors['terra-cotta']};
  margin-bottom: 8px;
  text-transform: uppercase;
`;

export const Title = styled.h2`
  font-family: ${({ theme }) => theme.fonts.oswald}, sans-serif;
  font-size: 28px;
  font-weight: normal;
  color: ${({ theme }) => theme.colors.dark};
  line-height: 1.2;
  margin: 0 0 10px;
  letter-spacing: normal;

  @media (max-width: 640px) {
    font-size: 24px;
  }
`;

export const SubText = styled.p`
  font-family: ${({ theme }) => theme.fonts.sfcompact_text}, system-ui,
    sans-serif;
  font-size: 14px;
  color: ${({ theme }) => theme.colors['slate-grey']};
  line-height: 1.55;
  margin: 0 0 18px;
  max-width: 500px;
`;

export const ValueNote = styled.div`
  background: #fff8f4;
  border: 1px solid #f3d3c6;
  border-radius: 6px;
  padding: 12px 14px;
  font-family: ${({ theme }) => theme.fonts.sfcompact_text}, system-ui,
    sans-serif;
  color: ${({ theme }) => theme.colors.dark};
  font-size: 13px;
  line-height: 1.45;
  margin-bottom: 24px;

  strong {
    color: ${({ theme }) => theme.colors.dark};
  }
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 22px;
`;

export const FieldLabel = styled.label`
  font-family: ${({ theme }) => theme.fonts.sfcompact_semibold}, system-ui,
    sans-serif;
  font-size: 11px;
  letter-spacing: 0.12em;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark};
  text-transform: uppercase;
`;

export const RequiredMark = styled.span`
  color: ${({ theme }) => theme.colors['terra-cotta']};
  margin-left: 3px;
`;

export const FieldHelp = styled.div`
  font-family: ${({ theme }) => theme.fonts.sfcompact_text}, system-ui,
    sans-serif;
  font-size: 12px;
  color: ${({ theme }) => theme.colors['slate-grey']};
  line-height: 1.45;
  margin-top: -2px;
`;

export const FieldInput = styled.input`
  border: 0;
  border-bottom: 1.5px solid ${({ theme }) => theme.colors['grey-two']};
  background: transparent;
  padding: 10px 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.dark};
  font-family: ${({ theme }) => theme.fonts.sfcompact_text}, system-ui,
    sans-serif;
  outline: none;
  transition: border-color 0.15s;

  &:focus {
    border-bottom-color: ${({ theme }) => theme.colors['terra-cotta']};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors['cool-grey']};
  }
`;

export const RoleGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-auto-rows: 1fr;
  gap: 10px;
  margin-top: 4px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

export const RoleCard = styled.button<{ $active?: boolean }>`
  border: 1.5px solid
    ${({ $active, theme }) =>
      $active ? theme.colors['terra-cotta'] : '#E4E4E4'};
  border-radius: 6px;
  padding: 14px 16px;
  background: ${({ $active }) => ($active ? '#FFF1E8' : '#fff')};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  font-family: inherit;
  transition: all 0.15s;
  height: 100%;
  ${({ $active, theme }) =>
    $active && `box-shadow: 0 0 0 1px ${theme.colors['terra-cotta']} inset;`}

  &:hover {
    border-color: ${({ theme }) => theme.colors['terra-cotta']};
    background: ${({ $active }) => ($active ? '#FFF1E8' : '#FFF8F4')};
  }
`;

export const RoleDot = styled.span<{ $active?: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid
    ${({ $active, theme }) => ($active ? theme.colors['terra-cotta'] : '#CCC')};
  flex-shrink: 0;
  position: relative;
  transition: border-color 0.15s;

  ${({ $active, theme }) =>
    $active &&
    `
    &::after {
      content: '';
      position: absolute;
      inset: 3px;
      background: ${theme.colors['terra-cotta']};
      border-radius: 50%;
    }
  `}
`;

export const RoleText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const RoleName = styled.span`
  font-family: ${({ theme }) => theme.fonts.sfcompact_semibold}, system-ui,
    sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.dark};
  letter-spacing: 0.02em;
`;

export const RoleDesc = styled.span`
  font-family: ${({ theme }) => theme.fonts.sfcompact_text}, system-ui,
    sans-serif;
  font-size: 11px;
  color: ${({ theme }) => theme.colors['slate-grey']};
  line-height: 1.25;
`;

export const ErrorText = styled.div`
  color: #b42318;
  font-family: ${({ theme }) => theme.fonts.sfcompact_text}, system-ui,
    sans-serif;
  font-size: 12px;
  line-height: 1.4;
`;

export const Footer = styled.div`
  padding: 20px 44px 28px;
  background: ${({ theme }) => theme.colors['white-two']};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  border-top: 1px solid ${({ theme }) => theme.colors['grey-two']};

  @media (max-width: 640px) {
    padding: 18px 24px 24px;
    flex-direction: column;
    align-items: stretch;
  }
`;

export const FooterNote = styled.div`
  font-family: ${({ theme }) => theme.fonts.sfcompact_text}, system-ui,
    sans-serif;
  font-size: 12px;
  color: ${({ theme }) => theme.colors['slate-grey']};
  line-height: 1.4;
  max-width: 285px;

  @media (max-width: 640px) {
    max-width: none;
  }
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    justify-content: space-between;
  }
`;

export const SkipButton = styled.button`
  font-family: ${({ theme }) => theme.fonts.sfcompact_text}, system-ui,
    sans-serif;
  font-size: 12px;
  color: ${({ theme }) => theme.colors['slate-grey']};
  text-decoration: underline;
  background: transparent;
  border: 0;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.colors.dark};
  }
`;

export const SubmitButton = styled.button`
  font-family: ${({ theme }) => theme.fonts.oswald}, sans-serif;
  background: ${({ theme }) => theme.colors['terra-cotta']};
  color: #fff;
  padding: 12px 28px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  border: 0;
  cursor: pointer;

  &:hover {
    opacity: 0.9;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;
